import { FormEvent, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, CalendarClock, CheckCircle2 } from 'lucide-react'
import { useAdminFrameworks } from '@/hooks/useAdminFrameworks'
import {
  RegulatoryDeadlineKind,
  useCreateRegulatoryDeadline,
} from '@/hooks/useAdminRegulatoryDeadlines'

const DEADLINE_KINDS: Array<{ value: RegulatoryDeadlineKind; label: string; help: string }> = [
  { value: 'deadline', label: 'Deadline', help: 'Final date to complete an obligation.' },
  { value: 'amendment', label: 'Amendment', help: 'Regulatory text or scope changes take effect.' },
  { value: 'transition', label: 'Transition', help: 'Implementation or grace-period milestone.' },
  { value: 'reporting', label: 'Reporting', help: 'Evidence, notice, or report submission due.' },
  { value: 'consultation_close', label: 'Consultation close', help: 'Public consultation or comment window closes.' },
]

type FormState = {
  frameworkId: string
  kind: RegulatoryDeadlineKind
  dueDate: string
  label: string
  description: string
  sourceUrl: string
}

const initialForm: FormState = {
  frameworkId: '',
  kind: 'deadline',
  dueDate: '',
  label: '',
  description: '',
  sourceUrl: '',
}

function buildSourceUrlPreview(input: string): { preview: string | null; error: string | null } {
  const trimmed = input.trim()
  if (trimmed.length === 0) return { preview: null, error: null }

  let parsed: URL
  try {
    parsed = new URL(trimmed)
  } catch {
    return { preview: null, error: 'Source URL must be a valid URL' }
  }

  if (parsed.protocol !== 'https:') {
    return { preview: null, error: 'Source URL must use HTTPS' }
  }
  if (parsed.username || parsed.password) {
    return { preview: null, error: 'Source URL must not include embedded credentials' }
  }

  // Preview the conservative browser-safe normalization. The API remains the
  // authority and applies the full regulator sanitizer on write.
  parsed.hash = ''
  parsed.search = ''
  return { preview: parsed.toString(), error: null }
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message
  return 'Could not create regulatory deadline'
}

export function AdminRegulatoryDeadlineEntryPage() {
  const frameworksQuery = useAdminFrameworks()
  const createDeadline = useCreateRegulatoryDeadline()
  const [form, setForm] = useState<FormState>(initialForm)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const sortedFrameworks = useMemo(
    () => [...(frameworksQuery.data?.frameworks ?? [])].sort((a, b) => a.sortOrder - b.sortOrder),
    [frameworksQuery.data?.frameworks],
  )

  const sourcePreview = useMemo(() => buildSourceUrlPreview(form.sourceUrl), [form.sourceUrl])

  const updateField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [field]: value }))
    setError(null)
    setMessage(null)
  }

  const validate = () => {
    if (!form.frameworkId) return 'Choose a regulatory framework'
    if (!form.dueDate) return 'Due date is required'
    if (!form.label.trim()) return 'Label is required'
    if (!form.sourceUrl.trim()) return 'Source URL is required'
    if (sourcePreview.error) return sourcePreview.error
    return null
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    try {
      const created = await createDeadline.mutateAsync({
        framework_id: form.frameworkId,
        kind: form.kind,
        due_date: form.dueDate,
        label: form.label.trim(),
        description: form.description.trim() ? form.description.trim() : null,
        source_url: form.sourceUrl.trim(),
      })
      setMessage(`Created deadline ${created.event_key}`)
      setForm(initialForm)
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6 px-4 py-6 md:px-6">
      <div>
        <Link
          to="/admin/regulatory-deadlines"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to deadlines
        </Link>
      </div>

      <header className="rounded-lg border bg-card p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="space-y-2">
            <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Regulatory admin</p>
            <h1 className="text-2xl font-bold tracking-tight">New regulatory deadline</h1>
            <p className="max-w-3xl text-sm text-muted-foreground">
              Add a manually verified deadline, amendment, transition, reporting, or consultation close milestone.
              The API validates the framework and applies authoritative source URL sanitisation on write.
            </p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border bg-background px-3 py-1 text-xs font-medium text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5" aria-hidden />
            Manual entry
          </div>
        </div>
      </header>

      {(error || message) && (
        <div
          role="alert"
          className={
            error
              ? 'rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive'
              : 'rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300'
          }
        >
          {message ? (
            <span className="inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              {message}
            </span>
          ) : error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid gap-6 rounded-lg border bg-card p-6 shadow-sm">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Framework" htmlFor="frameworkId" help="Admin framework id submitted as framework_id.">
            <select
              id="frameworkId"
              value={form.frameworkId}
              onChange={(event) => updateField('frameworkId', event.target.value)}
              disabled={frameworksQuery.isLoading}
              className="h-10 rounded-md border bg-background px-3 text-sm"
            >
              <option value="">Select framework</option>
              {sortedFrameworks.map((framework) => (
                <option key={framework.id} value={framework.id}>
                  {framework.name} · {framework.jurisdiction}
                </option>
              ))}
            </select>
            {frameworksQuery.isError && (
              <p className="text-xs text-destructive">Could not load admin frameworks.</p>
            )}
          </Field>

          <Field label="Deadline kind" htmlFor="kind" help={DEADLINE_KINDS.find((kind) => kind.value === form.kind)?.help}>
            <select
              id="kind"
              value={form.kind}
              onChange={(event) => updateField('kind', event.target.value as RegulatoryDeadlineKind)}
              className="h-10 rounded-md border bg-background px-3 text-sm"
            >
              {DEADLINE_KINDS.map((kind) => (
                <option key={kind.value} value={kind.value}>
                  {kind.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Due date" htmlFor="dueDate" help="Use the YYYY-MM-DD date sent as due_date.">
            <input
              id="dueDate"
              type="date"
              value={form.dueDate}
              onChange={(event) => updateField('dueDate', event.target.value)}
              className="h-10 rounded-md border bg-background px-3 text-sm"
            />
          </Field>

          <Field label="Label" htmlFor="label" help="Short analyst-facing title for the milestone.">
            <input
              id="label"
              value={form.label}
              onChange={(event) => updateField('label', event.target.value)}
              className="h-10 rounded-md border bg-background px-3 text-sm"
              placeholder="e.g. Consultation closes"
            />
          </Field>
        </div>

        <Field label="Description" htmlFor="description" help="Optional context stored with the deadline row.">
          <textarea
            id="description"
            value={form.description}
            onChange={(event) => updateField('description', event.target.value)}
            className="min-h-24 rounded-md border bg-background px-3 py-2 text-sm"
            placeholder="Summarise what changes or what evidence is due."
          />
        </Field>

        <Field
          label="Source URL"
          htmlFor="sourceUrl"
          help="HTTPS regulator source. Preview strips query strings and fragments; server-side sanitizer is authoritative."
        >
          <input
            id="sourceUrl"
            type="url"
            value={form.sourceUrl}
            onChange={(event) => updateField('sourceUrl', event.target.value)}
            className="h-10 rounded-md border bg-background px-3 text-sm"
            placeholder="https://regulator.example/deadlines"
          />
          {sourcePreview.preview && (
            <p className="text-xs text-muted-foreground">Preview: {sourcePreview.preview}</p>
          )}
          {sourcePreview.error && (
            <p className="text-xs text-destructive">{sourcePreview.error}</p>
          )}
        </Field>

        <div className="flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            Posts to <code>/admin/regulatory/deadlines</code>; duplicate event keys return a 409 from the API.
          </p>
          <button
            type="submit"
            disabled={createDeadline.isPending || frameworksQuery.isLoading}
            className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {createDeadline.isPending ? 'Creating…' : 'Create deadline'}
          </button>
        </div>
      </form>
    </div>
  )
}

function Field({
  label,
  htmlFor,
  help,
  children,
}: {
  label: string
  htmlFor: string
  help?: string
  children: React.ReactNode
}) {
  return (
    <div className="grid gap-2">
      <label htmlFor={htmlFor} className="text-sm font-medium text-foreground">
        {label}
      </label>
      {children}
      {help && <p className="text-xs text-muted-foreground">{help}</p>}
    </div>
  )
}
